// @flow

import * as React from 'react';
import { connect } from 'react-redux';

import classNames from '@ta/common.classnames';
import { Avatar } from '@ta/styleguide.avatar';
import { ResponsiveImage } from '@ta/media.image';
import { Link } from '@ta/common.routing';
import type { ViewportCategory } from '@ta/common.state';
import { DisplayUntagIcon } from '@ta/social.actions';
import { InteractionLogger } from '@ta/tracking.interactions';
import { POIObjectList } from '@ta/social.poi';
import { ContentDisplayDate } from '@ta/social.dates';
import { Linkify } from '@ta/social.links';
import { PhotoCarousel } from '@ta/common.photo-carousel';
import {
  SectionOverflowMenu,
  type SectionObject,
  type SectionOverflowItemType,
} from '@ta/social.section-overflow';

import type { PhotoWithMetaData } from './FeedSectionPhotoCarousel';
import styles from './SectionModal.less';

type ExternalProps = {|
  /** The list of image(s) and the accompanying metadata (caption) that will be displayed.
   * Passing only one image/metadata entry will render a single image, while passing an array of them will use the carousel */
  images: Array<PhotoWithMetaData>,
  /** which picture to show when the component renders */
  currentIndex: number,
  onPhotoChange?: (number, ?SyntheticEvent<*>) => void,
  actor: any,
  deleteObject?: ?() => void,
  untagObject?: ?() => void,
  showPOISavesIcon?: boolean,
  /** Additional content to add in the right hand rail under the caption */
  additionalContent?: ?React.Node,
  /** The social statistics bar */
  socialStatisticsBar: ?React.Node,
  /** The social action bar */
  socialActionBar: ?React.Node,
  /** This is the actual object that the overflow menu will be about */
  overflowMenuObject: SectionObject,
  overflowMenuObjectForType?: { [SectionOverflowItemType]: SectionObject },
|};

type Props = {|
  ...ExternalProps,
  loggedInUserId?: string,
  viewportCategory?: ViewportCategory,
|};

type State = {
  imageContainerWidth: number,
};

class SectionModal extends React.Component<Props, State> {
  static defaultProps = {
    deleteObject: null,
    untagObject: null,
    loggedInUserId: '',
    showPOISavesIcon: false,
    currentIndex: 0,
    additionalContent: null,
  };

  constructor(props: Props) {
    super(props);
    this.state = {
      imageContainerWidth: this.props.viewportCategory === 'MOBILE' ? 411 : 600,
    };
  }

  componentDidMount() {
    if (this.imageContainerRef?.current) {
      this.setState({
        imageContainerWidth: this.imageContainerRef.current.getBoundingClientRect().width,
      });
    }
  }

  imageContainerRef: React.ElementRef<*> = React.createRef();

  render() {
    const {
      actor,
      images,
      deleteObject,
      untagObject,
      viewportCategory,
      showPOISavesIcon,
      additionalContent,
      socialStatisticsBar,
      socialActionBar,
    } = this.props;

    const currentlyDisplayedImage = images[this.props.currentIndex];

    const displayDate =
      currentlyDisplayedImage?.publishedDate || currentlyDisplayedImage?.uploadDate;

    const overrideItems =
      actor && !actor.isMe
        ? {
            FOLLOW: true,
            UNTAG: !!DisplayUntagIcon(currentlyDisplayedImage, this.props.loggedInUserId),
          }
        : {
            FOLLOW: false,
            UNTAG: !!DisplayUntagIcon(currentlyDisplayedImage, this.props.loggedInUserId),
          };
    const isMobile = viewportCategory === 'MOBILE';
    const isTablet = viewportCategory === 'TABLET';
    const aspectAsPercent =
      currentlyDisplayedImage?.photoSizes?.length &&
      Math.round(
        currentlyDisplayedImage.photoSizes[currentlyDisplayedImage.photoSizes.length - 1].height /
          (currentlyDisplayedImage.photoSizes[currentlyDisplayedImage.photoSizes.length - 1].width /
            100),
      );

    const useCarousel = images.length > 1;
    const getModalHeight = () => {
      const last = currentlyDisplayedImage.photoSizes.length - 1;
      if ((isMobile || isTablet) && this.state.imageContainerWidth) {
        const heightOverWidth =
          currentlyDisplayedImage.photoSizes[last].height /
          currentlyDisplayedImage.photoSizes[last].width;
        return `${this.state.imageContainerWidth * heightOverWidth}px`;
      } else if (isMobile) {
        return '500px';
      } else if (isTablet) {
        // Fallback height
        return '550px';
      }

      return '100%';
    };

    const carousel = useCarousel && (
      <PhotoCarousel
        photos={images.map(photo => ({
          id: photo.id,
          photoSizes: photo.photoSizes,
        }))}
        displayIndex={this.props.currentIndex}
        height={getModalHeight()}
        width="100%"
        onPhotoChange={this.props.onPhotoChange}
        noCta
        totalCount={images.length}
        fitStrategy="contain"
        showIndicatorDots
        maxIndicatorDots={10}
        showProgress
        hideArrows
      />
    );

    /** We gain absolutely nothing for having the photo modal be lazy except wasting clock cycles */
    const singleImage =
      !useCarousel &&
      (isMobile || isTablet ? (
        <ResponsiveImage
          sources={currentlyDisplayedImage.photoSizes}
          width="100%"
          height="auto"
          padding={`0 0 ${aspectAsPercent}%`}
          notLazy
        />
      ) : (
        <ResponsiveImage
          sources={currentlyDisplayedImage.photoSizes}
          width="100%"
          height="100%"
          fitStrategy="contain"
          notLazy
        />
      ));

    return (
      <div className={`ui_columns ${styles.section_modal}`}>
        <div
          className={`ui_column is-8 is-9-widescreen  is-8-desktop is-12-tablet is-12-mobile ${
            styles.modal_image
          }`}
          ref={this.imageContainerRef}
        >
          {useCarousel ? carousel : singleImage}
        </div>
        <div
          className={`ui_column is-4 is-3-widescreen is-4-desktop is-12-tablet is-12-mobile ${
            styles.modal_info
          }`}
        >
          <div className={styles.section}>
            <div className={styles.member_block}>
              <div className={styles.member_block_left}>
                <Avatar photo={actor.avatar} display="inline" withBorder />
                <span className={styles.member_wrap}>
                  <InteractionLogger>
                    {logUserClick => (
                      <Link
                        to={{ page: 'Profile', username: actor.username }}
                        className={classNames(styles.member_name, { verified: actor.isVerified })}
                        onClick={e => logUserClick(e, 'userClick', { userId: actor.userId })}
                      >
                        {actor.displayName}
                      </Link>
                    )}
                  </InteractionLogger>
                  <span className={styles.event_date}>
                    <ContentDisplayDate dateStr={displayDate} />
                  </span>
                </span>
              </div>
              <div className={styles.member_block_right}>
                <SectionOverflowMenu
                  sectionObject={this.props.overflowMenuObject}
                  sectionObjectForType={this.props.overflowMenuObjectForType}
                  sectionActor={this.props.actor}
                  deleteObject={deleteObject}
                  untagObject={untagObject}
                  overrideItems={overrideItems}
                />
              </div>
            </div>

            <div className={styles.caption}>
              <InteractionLogger>
                {logInteraction => (
                  <Linkify
                    text={currentlyDisplayedImage.caption}
                    socialReferences={currentlyDisplayedImage.socialReferences}
                    useSpan
                    onLinkReferenceClick={(e, url) => {
                      logInteraction(e, 'linkReferenceClick', {
                        authorUserId: actor.userId,
                        url,
                      });
                    }}
                  />
                )}
              </InteractionLogger>
            </div>
            {currentlyDisplayedImage.caption && this.props.additionalContent && (
              <div className={styles.divider} />
            )}
            {additionalContent}
            {socialStatisticsBar}
            {socialActionBar}
          </div>
          {currentlyDisplayedImage?.location && (
            <POIObjectList
              pois={[currentlyDisplayedImage.location]}
              showSavesIcon={showPOISavesIcon}
            />
          )}
        </div>
      </div>
    );
  }
}

export default connect<Props, ExternalProps, _, _, _, _>(state => ({
  viewportCategory: state.meta.device.viewportCategory,
  loggedInUserId: state.auth.loggedInUserId,
}))(SectionModal);
